import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { AlertTriangle, Building2, Copy, KeyRound, Pause, Play, Plus, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import {
  AdminIssuedTenantCredential,
  AdminTenant,
  AdminTenantCredential,
  apiService,
} from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { buildTokenRequestBody, credentialStatus, expiryDateToIso } from './tenants/credentials';

const TENANT_PAGE_SIZE = 20;
const CREDENTIAL_PAGE_SIZE = 10;

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('de-DE');
};

const copyToClipboard = (value: string, label: string): void => {
  navigator.clipboard
    .writeText(value)
    .then(() => { toast.success(`${label} kopiert.`); })
    .catch(() => { toast.error('Kopieren fehlgeschlagen — bitte manuell markieren.'); });
};

/** Frisch ausgestellte Zugangsdaten samt Tenant-Namen für den Einmal-Dialog. */
interface IssuedSecretView {
  tenantName: string;
  credential: AdminIssuedTenantCredential;
}

export function TenantsPage() {
  // Tenant-Liste (backendseitige Suche + Pagination)
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);

  // Tenant anlegen
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createWithCredential, setCreateWithCredential] = useState(true);

  // Pausieren / Reaktivieren
  const [statusTarget, setStatusTarget] = useState<AdminTenant | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);

  // Zugangsdaten eines Tenants
  const [credentialsTenant, setCredentialsTenant] = useState<AdminTenant | null>(null);
  const [credentials, setCredentials] = useState<AdminTenantCredential[]>([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [credentialsPage, setCredentialsPage] = useState(1);
  const [credentialsTotalPages, setCredentialsTotalPages] = useState(1);
  const [credentialsReloadKey, setCredentialsReloadKey] = useState(0);
  const [issueName, setIssueName] = useState('');
  const [issueExpiry, setIssueExpiry] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<AdminTenantCredential | null>(null);
  const [revoking, setRevoking] = useState(false);

  // Einmal-Anzeige des Secrets
  const [issuedSecret, setIssuedSecret] = useState<IssuedSecretView | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      apiService
        .getAdminTenants({ search: search.trim() || undefined, page, limit: TENANT_PAGE_SIZE })
        .then((result) => {
          setTenants(result.data);
          setTotalPages(result.pagination.totalPages || 1);
        })
        .catch((err: unknown) => { toast.error(extractErrorMessage(err, 'Tenants konnten nicht geladen werden.')); })
        .finally(() => { setLoading(false); });
    }, 300);
    return () => { clearTimeout(timer); };
  }, [search, page, reloadKey]);

  useEffect(() => {
    if (!credentialsTenant) return;
    setCredentialsLoading(true);
    apiService
      .getAdminTenantCredentials(credentialsTenant.id, credentialsPage, CREDENTIAL_PAGE_SIZE)
      .then((result) => {
        setCredentials(result.data);
        setCredentialsTotalPages(result.pagination.totalPages || 1);
      })
      .catch((err: unknown) => { toast.error(extractErrorMessage(err, 'Zugangsdaten konnten nicht geladen werden.')); })
      .finally(() => { setCredentialsLoading(false); });
  }, [credentialsTenant, credentialsPage, credentialsReloadKey]);

  const resetCreateForm = (): void => {
    setCreateName('');
    setCreateDescription('');
    setCreateWithCredential(true);
  };

  const openCredentials = (tenant: AdminTenant): void => {
    setCredentials([]);
    setCredentialsPage(1);
    setIssueName('');
    setIssueExpiry('');
    setCredentialsTenant(tenant);
  };

  const handleCreate = async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();
    if (!createName.trim()) {
      toast.error('Bitte einen Namen eingeben.');
      return;
    }
    setCreating(true);
    try {
      const created = await apiService.createAdminTenant({
        name: createName.trim(),
        description: createDescription.trim() || undefined,
        createInitialCredential: createWithCredential,
      });
      toast.success(`Tenant „${created.tenant.name}" wurde angelegt.`);
      setCreateOpen(false);
      resetCreateForm();
      setPage(1);
      setReloadKey((k) => k + 1);
      if (created.initialCredential) {
        setIssuedSecret({ tenantName: created.tenant.name, credential: created.initialCredential });
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Tenant konnte nicht angelegt werden.'));
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (): Promise<void> => {
    if (!statusTarget) return;
    setStatusSaving(true);
    try {
      const updated = await apiService.setAdminTenantStatus(statusTarget.id, !statusTarget.isActive);
      setTenants((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      toast.success(`Tenant „${updated.name}" wurde ${updated.isActive ? 'reaktiviert' : 'pausiert'}.`);
      setStatusTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Status konnte nicht geändert werden.'));
    } finally {
      setStatusSaving(false);
    }
  };

  const handleIssue = async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();
    if (!credentialsTenant) return;
    const expiresAt = issueExpiry ? expiryDateToIso(issueExpiry) : undefined;
    if (issueExpiry && !expiresAt) {
      toast.error('Ungültiges Ablaufdatum.');
      return;
    }
    setIssuing(true);
    try {
      const issued = await apiService.createAdminTenantCredential(credentialsTenant.id, {
        name: issueName.trim() || undefined,
        expiresAt,
      });
      setIssueName('');
      setIssueExpiry('');
      setCredentialsPage(1);
      setCredentialsReloadKey((k) => k + 1);
      setIssuedSecret({ tenantName: credentialsTenant.name, credential: issued });
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Zugangsdaten konnten nicht erzeugt werden.'));
    } finally {
      setIssuing(false);
    }
  };

  const handleRevoke = async (): Promise<void> => {
    if (!credentialsTenant || !revokeTarget) return;
    setRevoking(true);
    try {
      await apiService.revokeAdminTenantCredential(credentialsTenant.id, revokeTarget.id);
      setCredentials((prev) => prev.map((c) => (c.id === revokeTarget.id ? { ...c, isActive: false } : c)));
      toast.success(`Zugangsdaten ${revokeTarget.keyPreview} wurden gesperrt.`);
      setRevokeTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Sperren fehlgeschlagen.'));
    } finally {
      setRevoking(false);
    }
  };

  const renderCredentialBadge = (credential: AdminTenantCredential) => {
    const status = credentialStatus(credential);
    if (status === 'revoked') return <Badge variant="destructive">Gesperrt</Badge>;
    if (status === 'expired') return <Badge variant="secondary">Abgelaufen</Badge>;
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Aktiv</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-600" />
            Tenants (External API)
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Partner und Reseller, die ihre Kunden über die External API verwalten. Hier werden Tenants angelegt und
            ihre Zugangsdaten (Client-ID + Secret) ausgestellt oder gesperrt. Nur Super-Admins haben Zugriff.
          </p>
        </div>
        <Button onClick={() => { setCreateOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Tenant anlegen
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tenants</CardTitle>
          <CardDescription>Suche nach Name oder ID.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            placeholder="Tenant suchen…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="max-w-sm"
          />
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">Lade Tenants…</div>
          ) : tenants.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">
              {search.trim() ? 'Keine Tenants zur Suche gefunden.' : 'Noch keine Tenants. Klicke „Tenant anlegen".'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Tenant-ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Angelegt am</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>
                      <div className="font-medium">{tenant.name}</div>
                      {tenant.description && <div className="text-xs text-gray-500">{tenant.description}</div>}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="font-mono text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                        onClick={() => { copyToClipboard(tenant.id, 'Tenant-ID'); }}
                        aria-label={`Tenant-ID von ${tenant.name} kopieren`}
                      >
                        {tenant.id}
                        <Copy className="w-3 h-3" />
                      </button>
                    </TableCell>
                    <TableCell>
                      {tenant.isActive ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Aktiv</Badge>
                      ) : (
                        <Badge variant="secondary">Pausiert</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-600">{formatDate(tenant.createdAt)}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={() => { openCredentials(tenant); }}>
                        <KeyRound className="w-4 h-4 mr-1" />
                        Zugangsdaten
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setStatusTarget(tenant); }}
                        aria-label={tenant.isActive ? `${tenant.name} pausieren` : `${tenant.name} reaktivieren`}
                      >
                        {tenant.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => { setPage((p) => p - 1); }}
            >
              Zurück
            </Button>
            <span className="text-xs text-gray-500">
              Seite {page} von {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => { setPage((p) => p + 1); }}
            >
              Weiter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tenant anlegen */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetCreateForm();
        }}
      >
        <DialogContent>
          <form onSubmit={(e) => { void handleCreate(e); }}>
            <DialogHeader>
              <DialogTitle>Tenant anlegen</DialogTitle>
              <DialogDescription>
                Ein Tenant ist ein Partner oder Reseller, der über die External API eigene Kunden anlegt und verwaltet.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="tenant-name">Name</Label>
                <Input
                  id="tenant-name"
                  placeholder="z.B. Starbüro GmbH"
                  value={createName}
                  onChange={(e) => { setCreateName(e.target.value); }}
                  maxLength={255}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tenant-description">Beschreibung (optional)</Label>
                <Input
                  id="tenant-description"
                  placeholder="z.B. Reseller Süd"
                  value={createDescription}
                  onChange={(e) => { setCreateDescription(e.target.value); }}
                  maxLength={255}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="tenant-initial-credential"
                  checked={createWithCredential}
                  onCheckedChange={(checked) => { setCreateWithCredential(checked === true); }}
                />
                <Label htmlFor="tenant-initial-credential" className="font-normal">
                  Direkt Zugangsdaten erzeugen
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); }}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Wird angelegt…' : 'Tenant anlegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pausieren / Reaktivieren */}
      <AlertDialog open={!!statusTarget} onOpenChange={(open) => { if (!open) setStatusTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusTarget?.isActive ? 'Tenant pausieren?' : 'Tenant reaktivieren?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusTarget?.isActive
                ? `„${statusTarget.name}" und alle seine Kunden und Bots erhalten keine neuen Zugangs-Tokens mehr. Laufende Bots und Gespräche sind nicht betroffen.`
                : `„${statusTarget?.name ?? ''}" kann wieder Zugangs-Tokens beziehen.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={statusSaving}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleStatusChange();
              }}
              disabled={statusSaving}
            >
              {statusSaving ? 'Wird gespeichert…' : statusTarget?.isActive ? 'Pausieren' : 'Reaktivieren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Zugangsdaten eines Tenants */}
      <Dialog open={!!credentialsTenant} onOpenChange={(open) => { if (!open) setCredentialsTenant(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Zugangsdaten — {credentialsTenant?.name}</DialogTitle>
            <DialogDescription>
              Mit Client-ID und Secret holt sich der Partner über <code>POST /v1/auth/token</code> einen Zugangs-Token
              (30 Minuten gültig). „Sofort sperren" macht auch bereits ausgestellte Tokens unmittelbar ungültig.
            </DialogDescription>
          </DialogHeader>

          <form
            className="flex flex-wrap items-end gap-3 border rounded-md p-3 bg-gray-50"
            onSubmit={(e) => { void handleIssue(e); }}
          >
            <div className="space-y-1 flex-1 min-w-[180px]">
              <Label htmlFor="credential-name">Bezeichnung (optional)</Label>
              <Input
                id="credential-name"
                placeholder="z.B. Produktion"
                value={issueName}
                onChange={(e) => { setIssueName(e.target.value); }}
                maxLength={255}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="credential-expiry">Gültig bis (optional)</Label>
              <Input
                id="credential-expiry"
                type="date"
                value={issueExpiry}
                onChange={(e) => { setIssueExpiry(e.target.value); }}
              />
            </div>
            <Button type="submit" disabled={issuing || credentialsTenant?.isActive === false}>
              <Plus className="w-4 h-4 mr-1" />
              {issuing ? 'Wird erzeugt…' : 'Neue Zugangsdaten'}
            </Button>
          </form>
          {credentialsTenant?.isActive === false && (
            <p className="text-xs text-amber-700">Der Tenant ist pausiert — zum Ausstellen erst reaktivieren.</p>
          )}

          {credentialsLoading ? (
            <div className="py-8 text-center text-sm text-gray-500">Lade Zugangsdaten…</div>
          ) : credentials.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">Noch keine Zugangsdaten für diesen Tenant.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bezeichnung</TableHead>
                  <TableHead>Client-ID / Secret</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Zuletzt genutzt</TableHead>
                  <TableHead>Gültig bis</TableHead>
                  <TableHead className="text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {credentials.map((credential) => (
                  <TableRow key={credential.id}>
                    <TableCell>{credential.name ?? '—'}</TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="font-mono text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                        onClick={() => { copyToClipboard(credential.id, 'Client-ID'); }}
                        aria-label="Client-ID kopieren"
                      >
                        {credential.id}
                        <Copy className="w-3 h-3" />
                      </button>
                      <div className="font-mono text-xs text-gray-400">{credential.keyPreview}</div>
                    </TableCell>
                    <TableCell>{renderCredentialBadge(credential)}</TableCell>
                    <TableCell className="text-gray-600">{formatDate(credential.lastUsedAt)}</TableCell>
                    <TableCell className="text-gray-600">{formatDate(credential.expiresAt)}</TableCell>
                    <TableCell className="text-right">
                      {credential.isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setRevokeTarget(credential); }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <ShieldOff className="w-4 h-4 mr-1" />
                          Sofort sperren
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={credentialsPage <= 1 || credentialsLoading}
              onClick={() => { setCredentialsPage((p) => p - 1); }}
            >
              Zurück
            </Button>
            <span className="text-xs text-gray-500">
              Seite {credentialsPage} von {credentialsTotalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={credentialsPage >= credentialsTotalPages || credentialsLoading}
              onClick={() => { setCredentialsPage((p) => p + 1); }}
            >
              Weiter
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sofort sperren */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => { if (!open) setRevokeTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Zugangsdaten sofort sperren?</AlertDialogTitle>
            <AlertDialogDescription>
              {revokeTarget && (
                <>
                  <strong className="font-mono">{revokeTarget.keyPreview}</strong> kann danach keine Tokens mehr
                  beziehen, und alle bereits ausgestellten Tokens werden sofort abgelehnt. Das lässt sich nicht
                  rückgängig machen — der Partner braucht danach neue Zugangsdaten.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleRevoke();
              }}
              disabled={revoking}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {revoking ? 'Wird gesperrt…' : 'Sofort sperren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Einmal-Anzeige des Secrets */}
      <Dialog open={!!issuedSecret} onOpenChange={(open) => { if (!open) setIssuedSecret(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Zugangsdaten für „{issuedSecret?.tenantName}"</DialogTitle>
            <DialogDescription className="flex items-start gap-2 text-amber-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              Das Secret wird nur jetzt angezeigt und ist danach nicht mehr abrufbar. Bitte sicher speichern und nur
              über einen sicheren Kanal an den Partner weitergeben.
            </DialogDescription>
          </DialogHeader>
          {issuedSecret && (
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label>Client-ID</Label>
                <div className="flex gap-2">
                  <Input readOnly value={issuedSecret.credential.clientId} className="font-mono text-xs" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { copyToClipboard(issuedSecret.credential.clientId, 'Client-ID'); }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Client-Secret</Label>
                <div className="flex gap-2">
                  <Input readOnly value={issuedSecret.credential.clientSecret} className="font-mono text-xs" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { copyToClipboard(issuedSecret.credential.clientSecret, 'Secret'); }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Anfrage-Body für POST /v1/auth/token</Label>
                <pre className="bg-gray-900 text-gray-100 text-xs rounded-md p-3 overflow-x-auto">
                  {buildTokenRequestBody(issuedSecret.credential.clientId, issuedSecret.credential.clientSecret)}
                </pre>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    copyToClipboard(
                      buildTokenRequestBody(issuedSecret.credential.clientId, issuedSecret.credential.clientSecret),
                      'Anfrage-Body',
                    );
                  }}
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Body kopieren
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setIssuedSecret(null); }}>Ich habe das Secret gespeichert</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
